import { defineStore } from 'pinia';
import { ref } from 'vue';

import type { FlashLevel, FlashMessage, FlashPayload } from '@shop/store/flash.types';

const FLASH_LEVELS: readonly FlashLevel[] = ['success', 'error', 'warning', 'info'];
const FLASH_COOKIE_NAME = 'flashes';
const FLASH_COOKIE_PATH = '/';

function normalizeLevel(level: string): FlashLevel {
  return (FLASH_LEVELS as readonly string[]).includes(level) ? (level as FlashLevel) : 'info';
}

function getCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  const match = document.cookie
    .split(';')
    .map(cookie => cookie.trim())
    .find(trimmed => trimmed.startsWith(prefix));

  return match ? match.slice(prefix.length) : undefined;
}

function removeCookie(name: string, path: string = FLASH_COOKIE_PATH): void {
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=${path}${secure}`;
}

function addMessagesForLevel(
  addMessage: (payload: FlashPayload) => void,
  level: string,
  items: unknown
): void {
  if (typeof items === 'string' && items !== '') {
    addMessage({ level: normalizeLevel(level), message: items });
    return;
  }

  if (Array.isArray(items)) {
    items.forEach(item => {
      if (typeof item === 'string' && item !== '') {
        addMessage({ level: normalizeLevel(level), message: item });
      }
    });
    return;
  }

  if (typeof items === 'object' && items !== null) {
    Object.values(items).forEach(item => {
      if (typeof item === 'string' && item !== '') {
        addMessage({ level: normalizeLevel(level), message: item });
      }
    });
  }
}

const useFlashStore = defineStore('flash', () => {
  const messages = ref<FlashMessage[]>([]);
  let nextId = 0;

  function add(payload: FlashPayload): void {
    nextId += 1;
    messages.value.push({ id: nextId, level: payload.level, message: payload.message });
  }

  function success(message: string): void {
    add({ level: 'success', message });
  }

  function error(message: string): void {
    add({ level: 'error', message });
  }

  function warning(message: string): void {
    add({ level: 'warning', message });
  }

  function info(message: string): void {
    add({ level: 'info', message });
  }

  function remove(id: number): void {
    messages.value = messages.value.filter(message => message.id !== id);
  }

  function clear(): void {
    messages.value = [];
  }

  function loadFromCookie(): void {
    const raw = getCookie(FLASH_COOKIE_NAME);
    if (!raw) {
      return;
    }

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(decodeURIComponent(raw)) as Record<string, unknown>;
    } catch {
      return;
    }

    Object.entries(parsed).forEach(([level, items]) => {
      addMessagesForLevel(add, level, items);
    });

    removeCookie(FLASH_COOKIE_NAME);
  }

  return {
    messages,
    add,
    success,
    error,
    warning,
    info,
    remove,
    clear,
    loadFromCookie,
  };
});

export default useFlashStore;
